import React, { useState, useEffect, useRef, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import styles from './styles.module.scss';
import { ConsentState, FormData, ProductType } from './types';
import SavingsSummary from './components/SavingsSummary';
import { useZipCodeCheck } from './hooks/useZipCodeCheck';
import { useSavingsCalculator } from './hooks/useSavingsCalculator';
import { submitLead, updateConsent } from './utils/api';
import {
  trackSlideAnswer,
  trackSliderSuccess,
  trackSliderFailure,
  trackLeadCreated,
  trackEnpalSourceCookie,
  trackAppDownloadClick,
} from './utils/tracking';
import { getUTMParams, splitFullName } from './utils/helpers';
import { OrganicIcons, renderIcon } from './components/OrganicIcons';
import {
  PRODUCT_OPTS,
  HOME_OPTS,
  TIMELINE_OPTS,
  DECISION_OPTS,
  TOTAL_STEPS,
  labelFor,
  mapProductToApi,
  mapToFormData,
  OrganicOption,
} from './config/organicOptions';

interface OrganicFields {
  product: string;
  timeline: string;
  home: string;
  cap: string;
  decision: string;
  name: string;
  email: string;
  phone: string;
}

const EMPTY: OrganicFields = {
  product: '',
  timeline: '',
  home: '',
  cap: '',
  decision: '',
  name: '',
  email: '',
  phone: '',
};

const validators = {
  cap: (v: string) => /^\d{5}$/.test(v.trim()),
  name: (v: string) => v.trim().length >= 2,
  email: (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim()),
  phone: (v: string) => v.replace(/[^\d]/g, '').length >= 9,
};

const ERR = {
  cap: 'Inserisci un CAP valido (5 cifre).',
  name: 'Inserisci nome e cognome.',
  email: "Controlla l'indirizzo email.",
  phone: 'Inserisci un numero di almeno 9 cifre.',
};

const SLIDE_TRACKING: Partial<Record<keyof OrganicFields, { id: string; title: string }>> = {
  product: { id: 'customer_type', title: 'Per quale prodotto vuoi scoprire i bonus?' },
  timeline: {
    id: 'install_timeline',
    title: 'Alcuni bonus hanno scadenza limitata, entro quando vorresti installare?',
  },
  home: { id: 'house_type', title: 'Per quale tipo di edificio vuoi scoprire i bonus?' },
  decision: { id: 'decision_stage', title: 'A che punto sei nella tua decisione di acquisto?' },
};

const FORM_SLIDE_META: Record<'name' | 'email' | 'phone', { id: string; title: string }> = {
  name: { id: 'name_form', title: 'Scopri il prossimo beneficio' },
  email: { id: 'email_form', title: 'Scopri il prossimo beneficio' },
  phone: { id: 'phone_form', title: '' },
};

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: () => void;
  error?: string;
  type?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  placeholder?: string;
  autoComplete?: string;
  maxLength?: number;
}

const PRIVACY_URL = 'https://www.enpal.com/it/protezione-dei-dati';

const EMPTY_CONSENT: ConsentState = {
  enpalMarketingConsent: false,
  thirdPartyMarketing: false,
  thirdPartyProfiling: false,
};

const PartnerConsentCard: React.FC<{
  checked: boolean;
  onChange: () => void;
  emphasize?: boolean;
  tone: 'light' | 'dark';
}> = ({ checked, onChange, emphasize, tone }) => (
  <label className={tone === 'light' ? styles.partnerCard : styles.preferenceCard}>
    <span className={tone === 'light' ? styles.partnerCardTop : styles.preferenceCardTop}>
      <span className={tone === 'light' ? styles.partnerTitle : styles.preferenceName}>
        Comunicazione dei dati ai partner
      </span>
      <input
        type="checkbox"
        className={`${tone === 'light' ? styles.consentCheckbox : styles.preferenceCheckbox} ${
          emphasize && !checked ? styles.consentCheckboxReady : ''
        }`}
        checked={checked}
        onChange={onChange}
      />
    </span>
    <span className={tone === 'light' ? styles.partnerDescription : styles.preferenceText}>
      Acconsento a comunicare i miei dati ai partner in{' '}
      <a
        className={tone === 'light' ? styles.privacyLink : styles.preferenceLink}
        href={PRIVACY_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(event) => event.stopPropagation()}
      >
        elenco
      </a>
      , per ricevere le loro offerte. Agiscono come titolari autonomi.
    </span>
  </label>
);

const Field: React.FC<FieldProps> = ({
  id,
  label,
  value,
  onChange,
  onBlur,
  error,
  type = 'text',
  inputMode,
  placeholder,
  autoComplete,
  maxLength,
}) => (
  <div className={styles.field}>
    <label htmlFor={id}>
      {label} <span className={styles.req}>*</span>
    </label>
    <input
      id={id}
      className={`${styles.input} ${error ? styles.inputErr : ''}`}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      type={type}
      inputMode={inputMode}
      placeholder={placeholder}
      autoComplete={autoComplete}
      maxLength={maxLength}
      aria-invalid={!!error}
      aria-describedby={error ? `${id}-e` : undefined}
    />
    {error && (
      <div className={styles.errmsg} id={`${id}-e`} role="alert">
        <OrganicIcons.help /> {error}
      </div>
    )}
  </div>
);

const SliderOrganicGeneric: React.FC = () => {
  const [step, setStep] = useState(0);
  const [fields, setFields] = useState<OrganicFields>(EMPTY);
  const [dir, setDir] = useState<'next' | 'prev'>('next');
  const [anim, setAnim] = useState(true);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitted, setSubmitted] = useState(false);
  const [leadUid, setLeadUid] = useState('');
  const [mongoId, setMongoId] = useState('');
  const [editingPartner, setEditingPartner] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);

  const { zipFlag, isValid: isCapValid, capFeedback, checkZip } = useZipCodeCheck();
  const { getBenefitText, getBonusCountText } = useSavingsCalculator(
    mapProductToApi(fields.product),
    zipFlag,
    fields.cap
  );

  const [consent, setConsent] = useState<ConsentState>(EMPTY_CONSENT);
  const phoneValid = validators.phone(fields.phone || '');

  useEffect(() => {
    const utmParams = getUTMParams();
    trackEnpalSourceCookie(utmParams.utm_source || 'organic');
  }, []);

  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
  }, [step, done]);

  useEffect(() => {
    setAnim(true);
    const t = setTimeout(() => setAnim(false), 360);
    return () => clearTimeout(t);
  }, [step]);

  const setField = (key: keyof OrganicFields, value: string) => {
    setFields((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'cap') {
        checkZip(value, mapProductToApi(next.product));
      }
      return next;
    });
  };

  const goNext = useCallback(() => {
    setDir('next');
    setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
  }, []);

  const goBack = useCallback(() => {
    setDir('prev');
    setStep((s) => Math.max(s - 1, 0));
  }, []);

  const markTouched = (k: string) => setTouched((p) => ({ ...p, [k]: true }));

  const errOf = (k: keyof typeof validators) => {
    const show = touched[k] || submitted;
    if (!show) return '';
    return validators[k](fields[k] || '') ? '' : ERR[k];
  };

  const addrValid = validators.cap(fields.cap || '');
  const incentiveCount = capFeedback?.bonusCount ?? 3;

  const pick = (key: keyof OrganicFields, val: string) => {
    setField(key, val);
    const meta = SLIDE_TRACKING[key];
    if (meta) trackSlideAnswer(meta.id, meta.title, val);
    if (step < TOTAL_STEPS - 1) {
      window.setTimeout(goNext, 280);
    }
  };

  const submitAddr = () => {
    setSubmitted(true);
    if (!addrValid || isCapValid === false) return;
    setSubmitted(false);
    trackSlideAnswer(
      'location_form',
      'Scopri gli incentivi e bonus nella tua zona',
      fields.cap
    );
    goNext();
  };

  const submitContacts = async () => {
    setLoading(true);
    const uid = uuidv4();
    setLeadUid(uid);
    const formData = mapToFormData(fields) as FormData;
    const submittedConsent: ConsentState = {
      enpalMarketingConsent: false,
      thirdPartyMarketing: consent.thirdPartyMarketing,
      thirdPartyProfiling: false,
    };
    setConsent(submittedConsent);

    try {
      const id = await submitLead(formData, uid, submittedConsent);
      const isLocalPreview =
        window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (id) {
        setMongoId(id);
        trackSliderSuccess();
        if (formData.email) {
          const { firstName, lastName } = splitFullName(formData.name || '');
          window.setTimeout(() => {
            trackLeadCreated(
              formData.email!,
              formData.phone || '',
              formData.zip || '',
              firstName,
              lastName
            );
          }, 120);
        }
        setDir('next');
        setDone(true);
      } else if (isLocalPreview) {
        setDir('next');
        setDone(true);
      } else {
        trackSliderFailure();
      }
    } catch (error) {
      console.error('Error submitting lead:', error);
      trackSliderFailure();
    } finally {
      setLoading(false);
    }
  };

  const submitFieldStep = (field: 'name' | 'email' | 'phone') => {
    setSubmitted(true);
    if (!validators[field](fields[field] || '')) return;
    setSubmitted(false);

    const meta = FORM_SLIDE_META[field];
    trackSlideAnswer(meta.id, meta.title, 'continue_button');

    if (field === 'phone') {
      submitContacts();
    } else {
      goNext();
    }
  };

  const handleConsentsChange = (next: ConsentState) => {
    setConsent(next);
    if (leadUid) {
      updateConsent(leadUid, next);
    }
  };


  const renderBenefitBanner = (slideId: string) => {
    const bonusText = getBonusCountText(slideId);
    const benefitText = getBenefitText(slideId);
    if (!bonusText && !benefitText) return null;

    return (
      <div className={styles.benefitBanner}>
        {bonusText && <span className={styles.eyebrow}>{bonusText}</span>}
        {benefitText && (
          <div className={styles.callout} role="status">
            <span className={styles.cbadge}>
              <OrganicIcons.euro />
            </span>
            <span>
              <b>{benefitText}</b>
            </span>
          </div>
        )}
      </div>
    );
  };

  const getSubmitLabel = (slideId: string) => {
    if (slideId === 'location_form') return 'Scopri i bonus';
    if (slideId === 'phone_form') return 'Scopri il risparmio totale';
    if (slideId === 'name_form' || slideId === 'email_form') {
      return 'Scopri il prossimo beneficio';
    }
    return 'Continua';
  };

  const optList = (opts: OrganicOption[], key: keyof OrganicFields, showSubtitle = false) => (
    <div className={styles.opts} role="radiogroup" aria-label="Opzioni">
      {opts.map((o) => {
        const sel = fields[key] === o.v;
        return (
          <button
            key={o.v}
            type="button"
            className={`${styles.opt} ${sel ? styles.optSelected : ''}`}
            role="radio"
            aria-checked={sel}
            onClick={() => pick(key, o.v)}
          >
            <span className={styles.ico}>{renderIcon(o.ico)}</span>
            <span className={styles.lab}>
              <b>{o.t}</b>
              {showSubtitle && o.s ? <small>{o.s}</small> : null}
            </span>
            {sel ? (
              <span className={styles.tick}>
                <OrganicIcons.check />
              </span>
            ) : (
              <span className={styles.chev}>
                <OrganicIcons.chev />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );

  if (done) {
    return (
      <div className={`${styles.slider} ${styles.isSuccess}`} role="region" aria-label="Richiesta inviata">
        <div className={styles.success}>
          <div className={styles.seal}>
            <OrganicIcons.check s={34} />
          </div>
          <h2 ref={headingRef} tabIndex={-1} className={styles.successTitle} style={{ outline: 'none' }}>
            Richiesta inviata! 🎉
          </h2>
          <p className={styles.lead}>
            Stiamo selezionando gli incentivi migliori a cui puoi accedere per risparmiare!
          </p>
          <div className={styles.recap}>
            <div className={styles.rrow}>
              <span>Prodotto</span>
              <b>{labelFor(PRODUCT_OPTS, fields.product) || '—'}</b>
            </div>
            <div className={styles.rrow}>
              <span>Installazione</span>
              <b>{labelFor(TIMELINE_OPTS, fields.timeline) || '—'}</b>
            </div>
            <div className={styles.rrow}>
              <span>Edificio</span>
              <b>{labelFor(HOME_OPTS, fields.home) || '—'}</b>
            </div>
            <div className={styles.rrow}>
              <span>Zona (CAP)</span>
              <b>{fields.cap || '—'}</b>
            </div>
            <div className={styles.rrow}>
              <span>Decisione</span>
              <b>{labelFor(DECISION_OPTS, fields.decision) || '—'}</b>
            </div>
            <div className={styles.rrow}>
              <span>Contatto</span>
              <b>{fields.name || '—'}</b>
            </div>
          </div>
          <div className={styles.savingsWrap}>
            <SavingsSummary
              productType={mapProductToApi(fields.product) as ProductType}
              zipFlag={zipFlag}
              zipCode={fields.cap}
            />
          </div>
          {mongoId && (
            <a
              href={`https://app.enpal.it/login?payload=${btoa(fields.phone || '')}`}
              className={styles.appCta}
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackAppDownloadClick}
            >
              Scarica l&apos;app per scoprire di più sui bonus
            </a>
          )}
          <section className={styles.preferences}>
            <h3 className={styles.preferencesTitle}>Le tue preferenze</h3>
            <p className={styles.preferencesHint}>Facoltativi, revocabili in ogni momento</p>

            <div className={styles.preferenceActions}>
              <button
                type="button"
                className={styles.preferenceBulk}
                onClick={() =>
                  handleConsentsChange({
                    ...consent,
                    enpalMarketingConsent: false,
                    thirdPartyProfiling: false,
                  })
                }
              >
                Deseleziona tutti
              </button>
              <button
                type="button"
                className={styles.preferenceBulk}
                onClick={() =>
                  handleConsentsChange({
                    ...consent,
                    enpalMarketingConsent: true,
                    thirdPartyProfiling: true,
                  })
                }
              >
                Seleziona tutti
              </button>
            </div>

            <label className={styles.preferenceCard}>
              <span className={styles.preferenceCardTop}>
                <span className={styles.preferenceName}>Offerte e novità Enpal</span>
                <input
                  type="checkbox"
                  className={styles.preferenceCheckbox}
                  checked={consent.enpalMarketingConsent}
                  onChange={() =>
                    handleConsentsChange({
                      ...consent,
                      enpalMarketingConsent: !consent.enpalMarketingConsent,
                    })
                  }
                />
              </span>
              <span className={styles.preferenceText}>
                Acconsento a ricevere offerte e novità da Enpal Italia via email, SMS, WhatsApp e
                telefono.
              </span>
            </label>

            <label className={styles.preferenceCard}>
              <span className={styles.preferenceCardTop}>
                <span className={styles.preferenceName}>Proposte su misura (profilazione)</span>
                <input
                  type="checkbox"
                  className={styles.preferenceCheckbox}
                  checked={consent.thirdPartyProfiling}
                  onChange={() =>
                    handleConsentsChange({
                      ...consent,
                      thirdPartyProfiling: !consent.thirdPartyProfiling,
                    })
                  }
                />
              </span>
              <span className={styles.preferenceText}>
                Acconsento che Enpal Italia usi consumi, dati dell&apos;immobile e interazioni per
                propormi offerte su misura.
              </span>
            </label>

            <div className={styles.partnerRow}>
              <span>
                Dati ai partner: <strong>{consent.thirdPartyMarketing ? 'Sì' : 'No'}</strong>
              </span>
              <button
                type="button"
                className={styles.modifyLink}
                onClick={() => setEditingPartner((open) => !open)}
              >
                Modifica
              </button>
            </div>

            {editingPartner && (
              <PartnerConsentCard
                tone="dark"
                checked={consent.thirdPartyMarketing}
                onChange={() =>
                  handleConsentsChange({
                    ...consent,
                    thirdPartyMarketing: !consent.thirdPartyMarketing,
                  })
                }
              />
            )}
          </section>
        </div>
      </div>
    );
  }

  let body: React.ReactNode;
  const showTrust = step >= 3;

  if (step === 0) {
    body = (
      <>
        <span className={styles.eyebrow}>
          <OrganicIcons.sun /> Scopri i bonus per cui sei idoneo in 30 secondi
        </span>
        <h2 className={styles.qTitle} ref={headingRef} tabIndex={-1}>
          Per quale prodotto vuoi scoprire i bonus?
        </h2>
        <p className={styles.qSub} />
        {optList(PRODUCT_OPTS, 'product', true)}
      </>
    );
  } else if (step === 1) {
    body = (
      <>
        <h2 className={styles.qTitle} ref={headingRef} tabIndex={-1}>
          Alcuni bonus hanno scadenza limitata, entro quando vorresti installare?
        </h2>
        <p className={styles.qSub} />
        {optList(TIMELINE_OPTS, 'timeline')}
      </>
    );
  } else if (step === 2) {
    body = (
      <>
        <h2 className={styles.qTitle} ref={headingRef} tabIndex={-1}>
          Per quale tipo di edificio vuoi scoprire i bonus?
        </h2>
        <p className={styles.qSub} />
        {optList(HOME_OPTS, 'home')}
      </>
    );
  } else if (step === 3) {
    const capOk = addrValid;
    body = (
      <>
        <h2 className={styles.qTitle} ref={headingRef} tabIndex={-1}>
          Scopri gli incentivi e bonus nella tua zona
        </h2>
        <p className={styles.qSub} />
        <Field
          id="cap"
          label="CAP"
          value={fields.cap}
          inputMode="numeric"
          maxLength={5}
          placeholder="Il tuo CAP"
          autoComplete="postal-code"
          onChange={(e) => setField('cap', e.target.value.replace(/[^\d]/g, ''))}
          onBlur={() => markTouched('cap')}
          error={errOf('cap')}
        />
        {isCapValid === true && capOk && (
          <div className={styles.callout} role="status" key="capfound">
            <span className={styles.cbadge}>
              <OrganicIcons.check s={14} />
            </span>
            <span>
              Ottima notizia! <b>Abbiamo trovato {incentiveCount} incentivi</b> a cui puoi accedere.
            </span>
          </div>
        )}
        <button type="button" className={styles.cta} onClick={submitAddr}>
          {getSubmitLabel('location_form')}{' '}
          <span className={styles.arrow}>
            <OrganicIcons.arrow />
          </span>
        </button>
      </>
    );
  } else if (step === 4) {
    body = (
      <>
        {renderBenefitBanner('decision_stage')}
        <h2 className={styles.qTitle} ref={headingRef} tabIndex={-1}>
          A che punto sei nella tua decisione di acquisto?
        </h2>
        <p className={styles.qSub} />
        {optList(DECISION_OPTS, 'decision')}
      </>
    );
  } else if (step === 5) {
    body = (
      <>
        {renderBenefitBanner('name_form')}
        <h2 className={styles.srOnly} ref={headingRef} tabIndex={-1}>
          Nome e cognome
        </h2>
        <Field
          id="name"
          label="Nome e cognome"
          value={fields.name}
          placeholder="Nome e cognome"
          autoComplete="name"
          onChange={(e) => setField('name', e.target.value)}
          onBlur={() => markTouched('name')}
          error={errOf('name')}
        />
        <button type="button" className={styles.cta} onClick={() => submitFieldStep('name')}>
          {getSubmitLabel('name_form')}{' '}
          <span className={styles.arrow}>
            <OrganicIcons.arrow />
          </span>
        </button>
      </>
    );
  } else if (step === 6) {
    body = (
      <>
        {renderBenefitBanner('email_form')}
        <h2 className={styles.srOnly} ref={headingRef} tabIndex={-1}>
          Email
        </h2>
        <Field
          id="email"
          label="Email"
          value={fields.email}
          type="email"
          inputMode="email"
          placeholder="Il tuo indirizzo email"
          autoComplete="email"
          onChange={(e) => setField('email', e.target.value)}
          onBlur={() => markTouched('email')}
          error={errOf('email')}
        />
        <button type="button" className={styles.cta} onClick={() => submitFieldStep('email')}>
          {getSubmitLabel('email_form')}{' '}
          <span className={styles.arrow}>
            <OrganicIcons.arrow />
          </span>
        </button>
      </>
    );
  } else {
    body = (
      <>
        {renderBenefitBanner('phone_form')}
        <h2 className={styles.srOnly} ref={headingRef} tabIndex={-1}>
          Telefono
        </h2>
        <Field
          id="phone"
          label="Numero di telefono"
          value={fields.phone}
          type="tel"
          inputMode="tel"
          placeholder="Numero di cellulare"
          autoComplete="tel"
          onChange={(e) => setField('phone', e.target.value)}
          onBlur={() => markTouched('phone')}
          error={errOf('phone')}
        />
        <p className={styles.consentHint}>Facoltativi, revocabili in ogni momento</p>
        <PartnerConsentCard
          tone="light"
          checked={consent.thirdPartyMarketing}
          emphasize={phoneValid}
          onChange={() =>
            setConsent((prev) => ({
              ...prev,
              thirdPartyMarketing: !prev.thirdPartyMarketing,
            }))
          }
        />
        <button
          type="button"
          className={styles.cta}
          onClick={() => submitFieldStep('phone')}
          disabled={!phoneValid || loading}
        >
          {loading ? (
            <>
              <span className={styles.spin} /> Invio in corso…
            </>
          ) : (
            <>
              {getSubmitLabel('phone_form')}{' '}
              <span className={styles.arrow}>
                <OrganicIcons.arrow />
              </span>
            </>
          )}
        </button>
        <p className={styles.callNote}>
          Ti chiameremo per il preventivo; la chiamata può essere registrata.{' '}
          <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
            Informativa privacy
          </a>
        </p>
      </>
    );
  }

  const fill = ((step + 1) / TOTAL_STEPS) * 100;

  return (
    <div className={styles.slider} role="region" aria-label="Calcola il tuo risparmio fotovoltaico">
      <div className={styles.head}>
        <div className={styles.headRow}>
          <button type="button" className={styles.back} onClick={goBack} hidden aria-label="Torna indietro">
            <OrganicIcons.back />
          </button>
          <span className={styles.stepcount}>
            Passo {step + 1} di {TOTAL_STEPS}
          </span>
          <span className={styles.brandmark}>
            Enpal<span className={styles.dot}>.</span>
          </span>
        </div>
        <div className={styles.bar}>
          <i className={styles.barFill} style={{ width: `${fill}%` }} />
        </div>
      </div>

      <div className={styles.viewport}>
        <div
          className={`${styles.panel} ${anim ? (dir === 'next' ? styles.animNext : styles.animPrev) : ''}`}
          key={step}
        >
          {body}
        </div>
      </div>

      {showTrust && (
        <div className={styles.trust}>
          <span>
            <OrganicIcons.users /> 100.000+ clienti in Italia
          </span>
          <span>
            <OrganicIcons.shield /> Zero anticipo
          </span>
          <span>
            <OrganicIcons.card /> Finanziamento su misura
          </span>
        </div>
      )}
    </div>
  );
};

export default SliderOrganicGeneric;
